/* eslint-disable react/no-inline-styles, no-inline-styles */
// Note: Inline styles are used for dynamic CSS custom properties which is standard React practice
import React, { useState, useMemo, useEffect, useRef, Suspense, lazy } from 'react';
import { apiFetch } from './services/api';
import {
    Student, Appointment, DayOfWeek, PlanTopic, Resource, ScaleLevel, ScaleDetail,
    User, UserRole, TaskStatus, NotificationItem, WeeklyPlan, ScaleResult, Message, Teacher,
    Parent, PlanningMode, GradeTopicConfig, YearlyRoadmap, EducationConsultant, ExamLibraryItem
} from './types';
import { SocketProvider, useSocket } from './contexts/socketContext';
import {
    HomeIcon,
    ChartBarIcon,
    CalendarIcon,
    BookOpenIcon,
    MapIcon,
    ClipboardListIcon,
    CollectionIcon,
    SearchIcon,
    LogoutIcon,
    UserGroupIcon,
    UserIcon,
    UserPlusIcon,
    LightBulbIcon,
    CheckCircleIcon,
    LockClosedIcon,
    XCircleIcon,
    PlusCircleIcon,
    TrashIcon,
    StarIcon,
    FireIcon,
    ClockIcon,
    BellIcon,
    UploadIcon,
    FileTextIcon,
    ChatIcon,
    AcademicCapIcon,
    MicrophoneIcon,
    PrinterIcon,
    PlayIcon,
    StopIcon,
    ExclamationTriangleIcon,
    PencilSquareIcon,
    VideoCameraIcon,
    ChevronLeftIcon,
    ChevronRightIcon,
    ChevronDownIcon,
    DeviceMobileIcon,
    MenuIcon,
    TargetIcon,
    BookmarkIcon,
    PhoneIcon,
    DotsVerticalIcon,
    PaperClipIcon,
    PaperAirplaneIcon,
    ExclamationIcon,
    PencilIcon,
    CameraIcon,
    EyeIcon,
    XMarkIcon,
    CalculatorIcon,
    GlobeIcon,
    LightningBoltIcon,
    BeakerIcon,
    SparklesIcon,
    BriefcaseIcon,
    XIcon,
    SupportIcon,
    SunIcon,
    MoonIcon,
    ArrowPathIcon,
    CheckBadgeIcon
} from './components/icons';

import {
    ONBOARDING_QUESTIONS,
    EXAM_TOPICS,
    MOCK_SCALES_EMPTY,
    MIDDLE_SCHOOL_SUBJECTS,
    HIGH_SCHOOL_SUBJECTS
} from './constants';
import { api, API_URL } from './services/api';
import { compressImage } from './services/imageCompression';
import {
    getStudyTipsFromGemini,
    generateStudentProfileReport,
    getTeacherResponse,
    generateLessonPlan,
    generateStudyNotes,
    generatePodcastAudio,
    playRawAudio
} from './services/geminiService';
import { LoginView } from './components/Auth/LoginView';
import { ForcePasswordChangeModal } from './components/Auth/ForcePasswordChangeModal';
import { SubjectCompletionWidget } from './components/SubjectCompletionWidget';
import { ConsultantExamManager } from './components/ConsultantExamManager';
import { ConsultantProfileView } from './components/ConsultantProfileView';
import { TargetAnalysisView } from './components/TargetAnalysisView';
import { ExamTrackingView } from './components/ExamTrackingView';
import { ResourcesView } from './components/ResourcesView';
import { ProfileView, ProfileAnalysisSection } from './components/ProfileView';
import TeacherProfileView from './components/TeacherProfileView';
import { ConsultantIncompleteTasksReport } from './components/ConsultantIncompleteTasksReport';
import { ConsultantStudentDetail, StudentInfoSection, VideoProgressSection } from './components/ConsultantStudentDetail';
import { NotificationProvider, NotificationToast, NotificationBell, useNotifications } from './components/NotificationSystem';
import { VoiceMessageSection } from './components/VoiceMessage';
import { ExamRunner } from './components/ExamRunner';
import { LandingPage } from './components/LandingPage';
import StudyHabitsDetailedReport from './components/StudyHabitsDetailedReport';
import PrivacyPolicy from './components/Legal/PrivacyPolicy';
import TermsOfService from './components/Legal/TermsOfService';
import { LoginModal } from './components/Auth/LoginModal';
// import { ForgotPasswordModal } from './components/Auth/ForgotPasswordModal'; // File missing


import { SmartDailyPlanner } from './components/SmartDailyPlanner';
import { WelcomeModal } from './components/Modals/WelcomeModal';
import { GoogleCalendarButton } from './components/GoogleCalendarButton';
import { PendingApprovalBanner } from './components/PendingApprovalBanner';

import { SupportView } from './components/SupportView';
import MeetingRatingModal from './components/MeetingRatingModal';
import { PackageSelectionScreen } from './components/PackageSelectionScreen';

import { SharedCampPlanView } from './components/SharedCampPlanView';

// Helper: auto-reload on chunk load failure after deploy
function lazyWithRetry(factory: () => Promise<any>) {
  return lazy(() =>
    factory().catch(async (err: any) => {
      const hasReloaded = sessionStorage.getItem('chunk_reload');
      if (!hasReloaded) {
        sessionStorage.setItem('chunk_reload', '1');
        if ('serviceWorker' in navigator) {
          try {
            const registrations = await navigator.serviceWorker.getRegistrations();
            for (let registration of registrations) {
              await registration.unregister();
            }
          } catch (e) {
            console.error('SW unregistration failed:', e);
          }
        }
        window.location.reload();
        return new Promise(() => {}); // never resolves, page is reloading
      }
      sessionStorage.removeItem('chunk_reload');
      throw err; // rethrow if already reloaded once
    })
  );
}

// Lazy loaded components for optimized initial page load
const AdminDashboard = lazyWithRetry(() => import('./components/Admin/AdminDashboard').then(m => ({ default: m.AdminDashboard })));
const EduAnalizWrapper = lazyWithRetry(() => import('./components/EduAnaliz/EduAnalizWrapper').then(m => ({ default: m.EduAnalizWrapper })));
const ParentDashboard = lazyWithRetry(() => import('./components/ParentDashboard'));
const CheckoutPage = lazyWithRetry(() => import('./components/CheckoutPage').then(m => ({ default: m.CheckoutPage })));
const CareerInventory = lazyWithRetry(() => import('./components/CareerInventory').then(m => ({ default: m.CareerInventory })));
const StudyHabitsAnalysis = lazyWithRetry(() => import('./components/StudyHabitsAnalysis').then(m => ({ default: m.StudyHabitsAnalysis })));
const OnboardingWizard = lazyWithRetry(() => import('./components/OnboardingWizard').then(m => ({ default: m.OnboardingWizard })));
const ConsultantDashboard = lazyWithRetry(() => import('./components/ConsultantDashboard').then(m => ({ default: m.ConsultantDashboard })));
const TeacherDashboard = lazyWithRetry(() => import('./components/TeacherDashboard').then(m => ({ default: m.TeacherDashboard })));

const LoadingFallback = () => (
    <div className="flex items-center justify-center min-h-screen bg-gray-50 dark:bg-[#020617]">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-indigo-500 border-t-transparent"></div>
    </div>
);
import TopicQuizButton from './components/TopicQuizButton';
import { EducatorLoginPage } from './components/EducatorLoginPage';
import { TopicQuizStatsCard } from './components/TopicQuizStatsCard';
import TeacherAssessmentDashboard from './components/TeacherAssessmentDashboard';
import TeacherClassManagement from './components/TeacherClassManagement';
import TopicQuizManagement from './components/Admin/TopicQuizManagement';
import { CookieConsent } from './components/CookieConsent';
import { EduAnalizLoginModal } from './components/EduAnaliz/EduAnalizLoginModal';
import { EduAnalizLanding } from './components/EduAnaliz/EduAnalizLanding';
import { EduAnalizVerifyEmail } from './components/EduAnaliz/EduAnalizVerifyEmail';
import { MOCK_EXAMS } from './mocks/mockExamData';
import {
    STUDY_GUIDES,
    LEARNING_METHODS,
    MOTIVATION_QUOTES,
    CAREER_GUIDE,
    FIELD_SELECTION_GUIDE,
    UNIVERSITY_INFO,
    SCALE_FEEDBACK
} from './mocks/supervisorContent';

import { ClassAssignmentsView } from './components/ClassAssignmentsView';
import { VirtualOpticalForm } from './components/VirtualOpticalForm';
import TopicQuizModal from './components/TopicQuizModal';
import { TopicHistoryView } from './components/TopicHistoryView';
import { Confetti, CelebrationOverlay, SadOverlay, SMSNotification } from './components/Overlays';
import { ProfileEditModal } from './components/Modals/ProfileEditModal';
import { YearlyRoadmapWidget } from './components/YearlyRoadmapWidget';
const StudentCommunicationView = lazyWithRetry(() => import('./components/StudentCommunicationView').then(m => ({ default: m.StudentCommunicationView })));
const SupervisorView = lazyWithRetry(() => import('./components/SupervisorView').then(m => ({ default: m.SupervisorView })));
const StudentHomeView = lazyWithRetry(() => import('./components/StudentHomeView').then(m => ({ default: m.StudentHomeView })));
const TeachView = lazyWithRetry(() => import('./components/TeachView').then(m => ({ default: m.TeachView })));
const DailyTasksView = lazyWithRetry(() => import('./components/DailyTasksView').then(m => ({ default: m.DailyTasksView })));
const StudyPlanView = lazyWithRetry(() => import('./components/StudyPlanView').then(m => ({ default: m.StudyPlanView })));
const WeeklyScheduleView = lazyWithRetry(() => import('./components/WeeklyScheduleView').then(m => ({ default: m.WeeklyScheduleView })));

const LOGO_URL = "/supervisor-logo-2026.png"; // Supervisor Logo 2026

// Helper to get correct API URL for local vs production
const getApiUrl = () => {
    const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    // In local, use port 3004. In production, use the relative redirect path.
    return isLocal
        ? `http://${window.location.hostname}:3004/api`
        : `/api`;
};


const generateMeetLink = () => {
    const part = (len: number) => Math.random().toString(36).substring(2, 2 + len);
    return `https://meet.google.com/${part(3)}-${part(4)}-${part(3)}`;
};

async function getValidMeetLink(educatorId: number | string, title: string, date: string, time: string) {
    try {
        // Check if educator is connected to Google
        const statusRes = await apiFetch(`${API_URL}/api/google/status/${educatorId}`);
        const statusData = await statusRes.json();

        if (statusData.connected) {
            // Generate a real Google Meet link
            const startTime = new Date(`${date}T${time}`).toISOString();
            const endTime = new Date(new Date(`${date}T${time}`).getTime() + 35 * 60000).toISOString(); // 35 min duration

            const meetingRes = await apiFetch(`${API_URL}/api/google/create-meeting`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    userId: educatorId,
                    title: title,
                    startTime,
                    endTime
                })
            });

            if (meetingRes.ok) {
                const meetingData = await meetingRes.json();
                return meetingData.meetLink;
            }
        }
    } catch (error) {
        console.error("Error creating Google meeting:", error);
    }

    return generateMeetLink();
}

// --- Helper Components ---

// Error Boundary to prevent component crashes from breaking the entire app
interface ErrorBoundaryProps {
    children: React.ReactNode;
    fallback?: React.ReactNode;
}

interface ErrorBoundaryState {
    hasError: boolean;
    error: Error | null;
}

// @ts-ignore - ErrorBoundary class component
class ErrorBoundary extends (React.Component as any) {
    state: ErrorBoundaryState = { hasError: false, error: null };

    constructor(props: ErrorBoundaryProps) {
        super(props);
        this.resetError = this.resetError.bind(this);
    }

    static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
        return { hasError: true, error };
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
        console.error('ErrorBoundary caught an error:', error, errorInfo);
        // Log to system error panel
        if ((window as any).addSystemError) {
            (window as any).addSystemError({
                type: 'error',
                source: 'React ErrorBoundary',
                message: error.message,
                details: `Component Stack: ${errorInfo.componentStack}`
            });
        } else {
            // Fallback if global function not yet loaded
            api.logSystemError({
                type: 'error',
                source: 'React ErrorBoundary (Fallback)',
                message: error.message,
                details: `Component Stack: ${errorInfo.componentStack}`
            });
        }
    }

    resetError() {
        this.setState({ hasError: false, error: null });
    }

    render(): React.ReactNode {
        const { hasError } = this.state;
        const { children, fallback } = this.props as ErrorBoundaryProps;

        if (hasError) {
            return fallback || (
                <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-xl border border-red-200 dark:border-red-800">
                    <p className="text-red-600 dark:text-red-400 text-sm font-medium">
                        Bu bileşen yüklenirken bir hata oluştu.
                    </p>
                    <button
                        onClick={this.resetError}
                        className="mt-2 text-xs text-red-500 underline"
                    >
                        Tekrar dene
                    </button>
                </div>
            );
        }
        return children;
    }
}








// ... (StudyPlanView, ExamTrackingView, ScalesView, ResourcesView)






// ResourcesView artık ./components/ResourcesView.tsx dosyasından import ediliyor



// Inline ConsultantDashboard and TeacherDashboard removed. Using lazy loaded versions from components/


const App = () => {
    const { socket, joinRoom, isConnected } = useSocket();
    // Checkout State
    const [showCheckoutPage, setShowCheckoutPage] = useState(false);
    const [selectedCheckoutPackageId, setSelectedCheckoutPackageId] = useState<string | null>(null);

    const [user, setUser] = useState<User | null>(() => {
        const storedUser = localStorage.getItem('supervisor_user');
        if (storedUser) {
            try {
                return JSON.parse(storedUser);
            } catch (error) {
                console.error("Failed to parse user from localStorage", error);
                localStorage.removeItem('supervisor_user');
            }
        }
        return null;
    });
    // Default to light mode (false), but check localStorage if available
    const [darkMode, setDarkMode] = useState(() => {
        const savedMode = localStorage.getItem('darkMode');
        return savedMode ? JSON.parse(savedMode) : false;
    });

    useEffect(() => {
        if (darkMode) {
            document.documentElement.classList.add('dark');
        } else {
            document.documentElement.classList.remove('dark');
        }
        localStorage.setItem('darkMode', JSON.stringify(darkMode));
    }, [darkMode]);

    // Persist login state
    useEffect(() => {
        if (user) {
            // Check for legacy educator sessions missing approvalStatus
            if ((user?.role === UserRole.TEACHER || user?.role === UserRole.EDUCATION_CONSULTANT) && !(user as any).approvalStatus) {
                const updatedUser = { ...user, approvalStatus: 'approved' }; // Default to approved for legacy
                setUser(updatedUser as any);
                localStorage.setItem('supervisor_user', JSON.stringify(updatedUser));
                return;
            }
            localStorage.setItem('supervisor_user', JSON.stringify(user));
        } else {
            // Only remove if it was explicitly set to null (logout)
            // Note: with lazy initializer, user is never null on mount if storage has data
            localStorage.removeItem('supervisor_user');
        }
    }, [user]);



    // Join socket room when user logs in
    useEffect(() => {
        if (user && user?.id) {
            joinRoom(user?.id);
        }
    }, [user?.id, isConnected, joinRoom]);

    // Handle incoming real-time messages
    useEffect(() => {
        if (!socket) return;

        const handleIncomingMessage = (data: { message: Message, studentId: number }) => {
            setStudents(prev => prev.map(s => {
                if (String(s.id) === String(data.studentId)) {
                    // Avoid duplicate messages
                    if (s.messages.some(m => m.id === data.message.id)) return s;
                    return { ...s, messages: [...s.messages, data.message] };
                }
                return s;
            }));

            // If the logged-in user is the current student being viewed or the recipient
            if (user?.role === UserRole.STUDENT && String(user?.id) === String(data.studentId)) {
                setUser(prev => {
                    if (!prev) return null;
                    const student = prev as Student;
                    if (student.messages.some(m => m.id === data.message.id)) return prev;
                    return { ...student, messages: [...student.messages, data.message] };
                });

            }

            // NOTE: Notifications are now handled by the server via socket 'notification' event
            // Removed redundant addNotification calls to prevent duplicate notifications
        };

        socket.on('receive_message', handleIncomingMessage);

        return () => {
            socket.off('receive_message');
        };
    }, [socket, user?.id]);
    const [adminUser, setAdminUser] = useState<User | null>(null); // Store admin user when viewing other role dashboards
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [activeView, setActiveView] = useState('home');
    const [activeQuizForOptical, setActiveQuizForOptical] = useState<string | null>(null);
    const [activeTopicQuiz, setActiveTopicQuiz] = useState<any | null>(null);
    const [teachViewSettings, setTeachViewSettings] = useState<{
        section?: 'videos' | 'questions' | 'exams',
        subject?: string,
        examType?: 'TYT' | 'AYT' | 'YDT',
        topic?: string
    } | null>(null);
    const [selectedStudentId, setSelectedStudentId] = useState<number | null>(null);
    const [educatorTab, setEducatorTab] = useState<'overview' | 'students' | 'messaging' | 'calendar' | 'exams' | 'profile' | 'incomplete_report' | 'camp_planner'>('overview');
    const [planningTab, setPlanningTab] = useState<'daily' | 'weekly' | 'tracker' | 'resources' | undefined>(undefined);
    const [students, setStudents] = useState<Student[]>([]);
    const [consultants, setConsultants] = useState<EducationConsultant[]>([]);
    const [teachers, setTeachers] = useState<Teacher[]>([]);
    const [parents, setParents] = useState<Parent[]>([
        {
            id: 401,
            name: "Ahmet Yılmaz",
            email: "ahmet.yilmaz@gmail.com",
            password: "veli123",
            role: UserRole.PARENT,
            studentIds: [1], // Elif Yılmaz'ın velisi
            phoneNumber: "0532 111 22 33",
            notifications: { sms: true, email: true }
        },
        {
            id: 402,
            name: "Fatma Dağ",
            email: "fatma.dag@gmail.com",
            password: "veli123",
            role: UserRole.PARENT,
            studentIds: [2], // Canan Dağ'ın velisi
            phoneNumber: "0533 222 33 44",
            notifications: { sms: true, email: false }
        }
    ]);
    const [examLibrary, setExamLibrary] = useState<ExamLibraryItem[]>([]);
    const [yearlyRoadmap, setYearlyRoadmap] = useState<YearlyRoadmap[]>([]);
    const [isRegistering, setIsRegistering] = useState(false);
    const [notifications, setNotifications] = useState<NotificationItem[]>([]);
    const [appointments, setAppointments] = useState<Appointment[]>([]);
    const [statsWeekOffset, setStatsWeekOffset] = useState(0);
    const [statsViewMode, setStatsViewMode] = useState<'weekly' | 'monthly'>('weekly');
    const [currentSMS, setCurrentSMS] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [showProfileModal, setShowProfileModal] = useState(false);
    const [showProfilePage, setShowProfilePage] = useState(false);
    const [activeExam, setActiveExam] = useState<any>(null);
    const [showNotificationsDropdown, setShowNotificationsDropdown] = useState(false);


    const [reviewExam, setReviewExam] = useState<{ exam: any, result: any } | null>(null);

    // Meeting Rating Modal State
    const [pendingRatingAppointment, setPendingRatingAppointment] = useState<Appointment | null>(null);
    const [skippedRatingIds, setSkippedRatingIds] = useState<number[]>([]);
    const [showEduAnalizLoginModal, setShowEduAnalizLoginModal] = useState(false);

    // Check for completed meetings that need rating (only for students)
    // DISABLED: Moved to manual trigger in past meetings history
    /*
    useEffect(() => {
        if (!user || user?.role !== UserRole.STUDENT) return;
    
        const checkForPendingRatings = () => {
            const now = new Date();
            // Find completed appointments without rating
            const unratedCompleted = appointments.find(apt => {
                // Skip if already rated or skipped
                if (apt.rating || skippedRatingIds.includes(apt.id)) return false;
                // Check if this is the student's appointment
                if (String(apt.studentId) !== String(user?.id)) return false;
    
                // Check if meeting time has passed (at least 30 minutes ago)
                const meetingDate = new Date(`${apt.date}T${apt.time}`);
                const timeDiff = now.getTime() - meetingDate.getTime();
                const minutesPassed = timeDiff / (1000 * 60);
    
                return minutesPassed >= 30; // Show rating after 30 mins
            });
    
            if (unratedCompleted && !pendingRatingAppointment) {
                setPendingRatingAppointment(unratedCompleted);
            }
        };
    
        // Check immediately and then every minute
        checkForPendingRatings();
        const interval = setInterval(checkForPendingRatings, 60000);
        return () => clearInterval(interval);
    }, [user, appointments, skippedRatingIds, pendingRatingAppointment]);
    */

    const [quizStats, setQuizStats] = useState<{
        totalQuizzes: number,
        totalQuestions: number,
        totalCorrect: number,
        totalIncorrect: number,
        totalUnanswered: number,
        averageScore: number,
        bySubject: {
            [subject: string]: {
                totalQuizzes: number,
                totalQuestions: number,
                totalCorrect: number,
                totalIncorrect: number,
                averageScore: number,
                topics: {
                    [topic: string]: {
                        totalCorrect: number,
                        totalIncorrect: number,
                        totalQuestions: number,
                        averageScore: number
                    }
                }
            }
        }
    } | null>(null);

    const fetchQuizStats = async (studentId?: string, examType?: 'TYT' | 'AYT' | 'YDT') => {
        const id = studentId || (user?.role === UserRole.STUDENT ? user?.id : null);
        if (!id) return;

        try {
            let url = `${getApiUrl()}/topic-quizzes/stats/${id}`;
            if (examType) {
                url += `?examType=${examType}`;
            }
            const response = await apiFetch(url);
            if (response.ok) {
                const data = await response.json();
                setQuizStats(data);
            }
        } catch (error) {
            console.error('İstatistik yükleme hatası:', error);
        }
    };

    useEffect(() => {
        if (user?.role === UserRole.STUDENT) {
            fetchQuizStats(String(user?.id));
        }
    }, [user?.id]);

    // Handle rating submission
    const handleSubmitRating = async (appointmentId: number, score: number, comment?: string) => {
        const appointmentIndex = appointments.findIndex(a => a.id === appointmentId);
        if (appointmentIndex === -1) return;

        const ratingData = {
            score,
            comment,
            ratedAt: new Date().toISOString(),
            ratedBy: user?.name || 'Anonim'
        };

        const updatedAppointment: Appointment = {
            ...appointments[appointmentIndex],
            rating: ratingData
        };

        // Update local state
        const newAppointments = [...appointments];
        newAppointments[appointmentIndex] = updatedAppointment;
        setAppointments(newAppointments);

        // Save to backend: find the consultant that owns this appointment and update
        try {
            const apt = appointments[appointmentIndex];
            const consultant = consultants.find(c => String(c.id) === String(apt.educatorId));
            if (consultant) {
                const updatedConsultantApts = (consultant.appointments || []).map(a =>
                    String(a.id) === String(appointmentId) ? { ...a, rating: ratingData } : a
                );
                const updatedConsultant = { ...consultant, appointments: updatedConsultantApts };
                await api.updateConsultant(updatedConsultant);
                setConsultants(prev => prev.map(c => String(c.id) === String(consultant.id) ? updatedConsultant : c));
            }
        } catch (error) {
            console.error("Failed to save rating to backend:", error);
        }

        setPendingRatingAppointment(null);
        addNotification(user?.id || 0, "Değerlendirmeniz için teşekkürler! ⭐", "success");
    };



    // Handle rating skip
    const handleSkipRating = () => {
        if (pendingRatingAppointment) {
            setSkippedRatingIds(prev => [...prev, pendingRatingAppointment.id]);
            setPendingRatingAppointment(null);
        }
    };

    // Handle no-show report from student
    const handleReportNoShow = async (appointmentId: number, reason?: string) => {
        const appointmentIndex = appointments.findIndex(a => a.id === appointmentId);
        if (appointmentIndex === -1) return;

        const noShowData = {
            score: 0,
            comment: `❌ Görüşme gerçekleşmedi${reason ? ': ' + reason : ''}`,
            ratedAt: new Date().toISOString(),
            ratedBy: user?.name || 'Anonim'
        };

        const updatedAppointment: Appointment = {
            ...appointments[appointmentIndex],
            status: 'no_show' as any,
            rating: noShowData
        };

        // Update local state
        const newAppointments = [...appointments];
        newAppointments[appointmentIndex] = updatedAppointment;
        setAppointments(newAppointments);

        // Save to backend
        try {
            const apt = appointments[appointmentIndex];
            const consultant = consultants.find(c => String(c.id) === String(apt.educatorId));
            if (consultant) {
                const updatedConsultantApts = (consultant.appointments || []).map(a =>
                    String(a.id) === String(appointmentId) ? { ...a, status: 'no_show', rating: noShowData } : a
                );
                const updatedConsultant = { ...consultant, appointments: updatedConsultantApts };
                await api.updateConsultant(updatedConsultant);
                setConsultants(prev => prev.map(c => String(c.id) === String(consultant.id) ? updatedConsultant : c));
            }
        } catch (error) {
            console.error('Failed to report no-show:', error);
        }

        setPendingRatingAppointment(null);
        addNotification(user?.id || 0, 'Geri bildiriminiz kaydedildi. Teşekkürler! 📝', 'success');
    };


    const handleStartExam = (type: 'TYT' | 'AYT' | 'YDT') => {
        // Find a mock exam of this type
        const mockExam = MOCK_EXAMS.find(e => e.type === type);
        if (mockExam) {
            setActiveExam(mockExam);
        } else {
            alert('Bu türde aktif bir deneme sınavı bulunamadı.');
        }
    };

    const handleReviewExam = (examResult: any) => {
        // Find the original exam setup to pass to runner
        // Try to get questions from the result first (new format)
        let questions = examResult.questions;

        // Fallback: If not in result, try to find in MOCK_EXAMS by name or type
        if (!questions || questions.length === 0) {
            const originalMock = MOCK_EXAMS.find(e => e.name === examResult.name || e.type === examResult.examType);
            if (originalMock) {
                questions = originalMock.questions;
            }
        }

        const examData = {
            id: examResult.examId || 'unknown',
            name: examResult.name,
            type: examResult.examType,
            duration: 0,
            questions: questions || []
        };

        if (!examData.questions || examData.questions.length === 0) {
            alert('Bu sınavın detaylı soru verisi bulunamadı.');
            return;
        }

        setReviewExam({ exam: examData, result: examResult });
    };

    const handleFinishExam = (result: { answers: { [questionId: number]: string }, duration: number }) => {
        if (!activeExam || !user) return;

        // Calculate score
        let correctCount = 0;
        let incorrectCount = 0;
        let emptyCount = 0;

        activeExam.questions.forEach((q: any) => {
            const answer = result.answers[q.id];
            if (answer) {
                if (answer === q.correctOption) {
                    correctCount++;
                } else {
                    incorrectCount++;
                }
            } else {
                emptyCount++;
            }
        });

        // Calculate detailed stats
        const subjectStats: Record<string, { correct: number, incorrect: number, empty: number }> = {};
        const topicStats: Record<string, { correct: number, incorrect: number, empty: number }> = {};

        activeExam.questions.forEach(q => {
            const answer = result.answers[q.id];
            const isCorrect = answer === q.correctOption;
            const isEmpty = !answer;
            const isIncorrect = !isEmpty && !isCorrect;

            // Init stats
            if (!subjectStats[q.subject]) subjectStats[q.subject] = { correct: 0, incorrect: 0, empty: 0 };
            const topicKey = `${q.subject} - ${q.topic}`; // Use composite key for unique tracking
            if (!topicStats[topicKey]) topicStats[topicKey] = { correct: 0, incorrect: 0, empty: 0 };

            // Update stats
            if (isCorrect) {
                subjectStats[q.subject].correct++;
                topicStats[topicKey].correct++;
            } else if (isIncorrect) {
                subjectStats[q.subject].incorrect++;
                topicStats[topicKey].incorrect++;
            } else {
                subjectStats[q.subject].empty++;
                topicStats[topicKey].empty++;
            }
        });

        // Format for TrialExamResult
        const scores: Record<string, any> = {};
        Object.entries(subjectStats).forEach(([subject, stat]) => {
            scores[subject] = {
                ...stat,
                net: stat.correct - (stat.incorrect * 0.25)
            };
        });

        const topicAnalysis = Object.entries(topicStats).map(([key, stat]) => {
            const [subject, ...topicParts] = key.split(' - ');
            const topic = topicParts.join(' - ');
            return {
                subject,
                topic,
                ...stat,
                net: stat.correct - (stat.incorrect * 0.25)
            };
        });

        const net = correctCount - (incorrectCount * 0.25);

        // Creates a summary result object
        const newResult: any = {
            id: Date.now(),
            name: activeExam.name,
            examType: activeExam.type,
            date: new Date().toISOString(),
            scores: scores,
            topicAnalysis: topicAnalysis,
            studentAnswers: result.answers,
            examId: activeExam.id, // Link to original exam if needed
            title: activeExam.name, // Ensure naming consistency
            questions: activeExam.questions, // Save questions for review mode
            totalNet: net,
            totalCorrect: correctCount,
            totalIncorrect: incorrectCount,
            totalEmpty: emptyCount,
            durationUsed: result.duration // Save the actual duration used (in seconds)
        };

        // Update student
        const updatedStudent = {
            ...user,
            trialExams: [...(user as Student).trialExams, newResult]
        };
        handleUpdateStudent(updatedStudent as Student);

        setActiveExam(null);
        setActiveView('exams');
        // Using window.alert for now, could be a better modal
        // addNotification is not directly available here unless we use a context hook inside a child, 
        // but since this is App.tsx we might not have access to the context created in the wrapper yet 
        // if we were inside the provider. 
        // Actually App is wrapped by NotificationProvider in the export default, so we can't use useNotifications logic 
        // directly inside App unless we move App content to a child or pass addNotification prop.
        // Wait, I define App component then wrap it.
        // App component itself can't use useNotifications hook if the provider wraps it *outside*.
        // Correct.
        // However, I can trigger a notification if I passed the addNotification context down or just use alert for now.
        // I will use alert for simplicity.
        alert(`Sınav tamamlandı! Netiniz: ${net}`);
    };

    const fetchData = React.useCallback(async () => {
        try {
            const [studentsData, consultantsData, teachersData, examLibraryData, yearlyRoadmapData] = await Promise.all([
                api.getStudents(),
                api.getConsultants(),
                api.getTeachers(),
                api.getExamLibrary(),
                api.getYearlyRoadmap()
            ]);
            setStudents(studentsData);
            setConsultants(consultantsData);
            setTeachers(teachersData);
            setExamLibrary(examLibraryData);
            setYearlyRoadmap(yearlyRoadmapData);

            // Initialize appointments from ALL consultants and teachers
            const allAppointments: Appointment[] = [
                ...consultantsData.flatMap(c => (c.appointments || []).map(a => ({ ...a, educatorId: a.educatorId || c.id }))),
                ...teachersData.flatMap(t => (t.appointments || []).map(a => ({ ...a, educatorId: a.educatorId || t.id })))
            ];
            setAppointments(allAppointments);

            // Refresh current user data from the freshly fetched lists
            if (user) {
                const currentStudent = studentsData.find(s => String(s.id) === String(user?.id));
                const currentConsultant = consultantsData.find(c => String(c.id) === String(user?.id));
                const currentTeacher = teachersData.find(t => String(t.id) === String(user?.id));

                const updatedUser = currentStudent || currentConsultant || currentTeacher;
                if (updatedUser) {
                    setUser(updatedUser);
                    // Update localStorage too
                    const { password, ...safeUser } = updatedUser as any;
                    localStorage.setItem('supervisor_user', JSON.stringify(safeUser));
                }
            }
        } catch (error) {
            console.error("Failed to fetch data:", error);
        } finally {
            setLoading(false);
        }
    }, [user?.id]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // Socket Listener for Real-time Updates
    useEffect(() => {
        if (!socket) return;

        socket.on('data_updated', (data: any) => {
            console.log('Real-time update received:', data);
            fetchData();
        });

        socket.on('notification', (notif: any) => {
            console.log('Real-time notification received:', notif);
            if (notif && notif.message) {
                // targetUserId, msg, type
                addNotification(notif.userId || user?.id, notif.message, notif.type || 'info', notif.action);
            }
        });

        socket.on('messages_read', (data: any) => {
            const { readerId } = data;
            // Update local state to show green ticks
            if (user?.role === UserRole.STUDENT) {
                // Student's view: Consultant read my messages
                setStudents(prev => prev.map(s => {
                    if (String(s.id) === String(user?.id)) {
                        return {
                            ...s,
                            messages: s.messages.map(m => !m.isAdmin ? { ...m, read: true } : m)
                        };
                    }
                    return s;
                }));
            } else {
                // Consultant's view: Student read my messages
                setStudents(prev => prev.map(s => {
                    if (String(s.id) === String(readerId)) {
                        return {
                            ...s,
                            messages: s.messages.map(m => m.isAdmin ? { ...m, read: true } : m)
                        };
                    }
                    return s;
                }));
            }
        });

        socket.on('receive_message', (data: any) => {
            console.log('📩 Real-time message received:', data);
            // Re-fetch data to show the new message in the chat
            fetchData();
        });

        return () => {
            socket.off('data_updated');
            socket.off('notification');
            socket.off('messages_read');
            socket.off('receive_message');
        };
    }, [socket, fetchData, user?.id, isConnected]);

    const handleUpdateStudent = async (updatedStudent: Student) => {
        try {
            await api.updateStudent(updatedStudent);
            setStudents(prev => prev.map(s => String(s.id) === String(updatedStudent.id) ? updatedStudent : s));

            // Fix: Update user state if it matches the current user
            if (user && String(user?.id) === String(updatedStudent.id)) {
                setUser(updatedStudent);
                // Also update localStorage so changes persist across reloads
                const { password, ...safeUser } = updatedStudent as any;
                localStorage.setItem('supervisor_user', JSON.stringify(safeUser));
            }

            // Real-time notification: Notify the relevant users about data change
            if (socket && isConnected) {
                // Notify the student (if educator is updating, or student is updating self)
                socket.emit('trigger_data_update', { userId: updatedStudent.id });

                // If the current user is a student, also notify their assigned consultant
                if (user && user?.id === updatedStudent.id && user?.role === UserRole.STUDENT) {
                    const studentUser = user as Student;
                    if (studentUser.assignedConsultantId) {
                        socket.emit('trigger_data_update', { userId: studentUser.assignedConsultantId });
                    }
                }
            }
        } catch (error) {
            console.error("Failed to update student:", error);
            // Sistem hataları sadece admin'e gider (userId: 0)
            addNotification(0, "Değişiklikler kaydedilemedi!", "warning");
        }
    };

    const handleSaveExam = async (exam: ExamLibraryItem) => {
        try {
            const savedExam = await api.createExam(exam);
            setExamLibrary(prev => [...prev, savedExam]);
            addNotification(user?.id || 0, "Deneme başarıyla kaydedildi.", "success");
        } catch (error) {
            console.error("Failed to save exam:", error);
            // Sistem hataları sadece admin'e gider (userId: 0)
            addNotification(0, "Deneme kaydedilemedi!", "warning");
        }
    };

    const handleLogin = (loggedInUser: User) => {
        if (!loggedInUser) return;
        setUser(loggedInUser);
        // Güvenlik önlemi: Şifreyi tarayıcı hafızasına kaydetmeden önce çıkarıyoruz
        const { password, ...safeUser } = loggedInUser as any;
        localStorage.setItem('supervisor_user', JSON.stringify(safeUser));

        // Unread message check for students
        if (loggedInUser.role === UserRole.STUDENT) {
            const student = loggedInUser as Student;
            if (student.messages) {
                // Check if there are unread messages from admin/consultant (isAdmin=true)
                // AND the message is older than 10 seconds (to prevent duplication with real-time socket notifications)
                const now = Date.now();
                const unreadCount = student.messages.filter(m =>
                    m.isAdmin &&
                    !m.read &&
                    (now - new Date(m.timestamp).getTime() > 10000)
                ).length;

                if (unreadCount > 0) {
                    setTimeout(() => {
                        addNotification(loggedInUser.id, `${unreadCount} okunmamış mesajınız var.`, 'info', { type: 'view_messages' });
                    }, 1000); // Small delay to ensure UI is ready
                }
            }
        }
        // Unread message check for consultants
        else if (loggedInUser.role === UserRole.EDUCATION_CONSULTANT) {
            // This requires scanning all students which might be expensive here
            // Rely on the dashboard indicator for consultants instead
        }
    };

    const handleLogout = () => {
        const currentPath = window.location.pathname;
        const isAdmin = user?.role === UserRole.ADMIN;
        const isEducator = user?.role === UserRole.TEACHER || user?.role === UserRole.EDUCATION_CONSULTANT;

        setUser(null);
        localStorage.removeItem('supervisor_user');

        // Stay on the relevant sub-route if logging out from there
        if (isAdmin && (currentPath.startsWith('/admin') || window.location.hash.includes('admin'))) {
            window.location.href = '/admin-login';
        } else if (isEducator && (currentPath.startsWith('/educator') || window.location.hash.includes('educator'))) {
            window.location.href = '/educator';
        } else {
            window.location.href = '/';
        }
    };

    const handleUpdateProfile = async (updatedUser: User) => {
        try {
            let result;
            if (updatedUser.role === UserRole.STUDENT) {
                result = await api.updateStudent(updatedUser as Student);
                setStudents(prev => prev.map(s => s.id === result.id ? result : s));
            } else if (updatedUser.role === UserRole.TEACHER) {
                result = await api.updateTeacher(updatedUser as Teacher);
                setTeachers(prev => prev.map(t => t.id === result.id ? result : t));
                // Sync appointments from teacher
                const teacherWithApts = updatedUser as Teacher;
                if (teacherWithApts.appointments) {
                    setAppointments(prev => {
                        const otherApts = prev.filter(a => String(a.educatorId) !== String(result.id));
                        return [...otherApts, ...teacherWithApts.appointments.map(a => ({ ...a, educatorId: result.id }))];
                    });
                }
            } else if (updatedUser.role === UserRole.EDUCATION_CONSULTANT) {
                result = await api.updateConsultant(updatedUser as EducationConsultant);
                setConsultants(prev => prev.map(c => c.id === result.id ? result : c));
                // Sync appointments from consultant
                const consultantWithApts = updatedUser as EducationConsultant;
                if (consultantWithApts.appointments) {
                    setAppointments(prev => {
                        const otherApts = prev.filter(a => String(a.educatorId) !== String(result.id));
                        return [...otherApts, ...consultantWithApts.appointments.map(a => ({ ...a, educatorId: result.id }))];
                    });
                }
            }

            if (result) {
                setUser(result);
                // Also update localStorage so changes persist across reloads
                const { password, ...safeUser } = result as any;
                localStorage.setItem('supervisor_user', JSON.stringify(safeUser));
                addNotification(result.id, "Profil bilgileriniz güncellendi.", "success");
            }
        } catch (error) {
            console.error("Failed to update profile:", error);
            addNotification(user?.id || 0, "Profil güncellenemedi!", "warning");
        }
    };

    const handleRegistrationComplete = () => {
        setIsRegistering(false);
        addNotification(0, "Kayıt başarılı! Lütfen giriş yapınız.", "success");
    };

    const handleToggleTopic = async (studentId: number, topicKey: string, topicRes?: string, questionRes?: string, questionStats?: { solved: number; correct: number; incorrect: number }) => {
        const student = students.find(s => s.id === studentId);
        if (!student) return;

        const details = { ...student.completedTopicDetails };
        const existingDetail = details[topicKey] || {
            date: new Date().toISOString().split('T')[0],
            topicResourceName: '',
            questionResourceName: '',
            topicCompleted: false,
            questionCompleted: false,
            questionStats: undefined
        };

        // Eğer hem topicRes hem questionRes undefined ise -> bu sıfırlama isteği
        if (topicRes === undefined && questionRes === undefined && questionStats === undefined) {
            delete details[topicKey];
        } else {
            // Konu kaynağı güncelleme
            if (topicRes !== undefined) {
                existingDetail.topicResourceName = topicRes;
                existingDetail.topicCompleted = true;
            }

            // Soru kaynağı güncelleme
            if (questionRes !== undefined) {
                existingDetail.questionResourceName = questionRes;
                existingDetail.questionCompleted = true;
            }

            // Soru istatistikleri güncelleme (birikimli)
            if (questionStats) {
                const oldStats = existingDetail.questionStats || { solved: 0, correct: 0, incorrect: 0 };
                existingDetail.questionStats = {
                    solved: oldStats.solved + questionStats.solved,
                    correct: oldStats.correct + questionStats.correct,
                    incorrect: oldStats.incorrect + questionStats.incorrect
                };
            }

            existingDetail.date = new Date().toISOString().split('T')[0];
            details[topicKey] = existingDetail;
        }

        // completedTopics listesi artık sadece "tamamen tamamlanmış" konuları içerir
        const completed = Object.keys(details).filter(key => {
            const d = details[key];
            return d.topicCompleted && d.questionCompleted;
        });

        const updatedStudent = { ...student, completedTopics: completed, completedTopicDetails: details };
        await handleUpdateStudent(updatedStudent);
    };

    const handleToggleResourceTopic = async (studentId: number, subject: string, topicKey: string, resourceId: string) => {
        const student = students.find(s => s.id === studentId);
        if (!student) return;

        const tracking = { ...(student.topicTracking || {}) };
        const subjectTracking = { ...(tracking[subject] || {}) };
        const topicTracking = { ...(subjectTracking[topicKey] || {}) };
        const isCompleted = topicTracking[resourceId];

        topicTracking[resourceId] = !isCompleted;
        subjectTracking[topicKey] = topicTracking;
        tracking[subject] = subjectTracking;

        const updatedStudent = { ...student, topicTracking: tracking };
        await handleUpdateStudent(updatedStudent);
    };

    const handleSendMessage = async (studentId: number, text: string, isAdmin: boolean = false) => {
        const student = students.find(s => String(s.id) === String(studentId));
        if (!student) return;

        // Use actual user's info instead of hardcoded values
        const senderId = isAdmin && user ? (typeof user?.id === 'string' ? parseInt(user?.id) : user?.id) : studentId;
        const senderName = isAdmin && user ? user?.name : student.name;

        const newMessage: Message = {
            id: Date.now(),
            senderId: senderId,
            senderName: senderName,
            content: text,
            timestamp: new Date().toISOString(),
            read: false,
            isAdmin: isAdmin
        };
        const updatedStudent = { ...student, messages: [...student.messages, newMessage] };
        await handleUpdateStudent(updatedStudent);

        // Emit message via socket for real-time delivery
        if (socket && isConnected) {
            socket.emit('send_message', {
                to: isAdmin ? studentId : student.assignedConsultantId,
                message: newMessage,
                studentId: studentId, // Context student
                senderName: senderName // For notification display
            });
        }

        // NOTE: Notifications are now handled by the server via socket 'notification' event
        // Removed redundant local addNotification calls to prevent duplicate notifications
    };

    const handleNotificationClick = (n: any) => {
        if (!n.action) return;

        if (n.action.type === 'view_messages') {
            if (user?.role === UserRole.STUDENT) {
                setActiveView('communication');
                // Mark messages as read when clicking the notification
                handleMarkStudentMessagesRead(user?.id);
            } else if (user?.role === UserRole.EDUCATION_CONSULTANT || user?.role === UserRole.TEACHER) {
                setEducatorTab('messaging');
                if (n.action.studentId) {
                    const student = students.find(s => String(s.id) === String(n.action.studentId));
                    if (student) {
                        setSelectedStudentId(student.id);
                        handleMarkConsultantMessagesRead(student.id);
                    }
                }
            }
        }

        setShowNotificationsDropdown(false);
        // Optionally mark as read when clicked
        setNotifications(prev => prev.map(notif =>
            notif.id === n.id ? { ...notif, read: true } : notif
        ));
    };

    const handleMarkStudentMessagesRead = async (studentId: number) => {
        // Change: DELETE message notifications instead of just marking them read
        // Filter out notifications that are for this user AND are related to messages
        setNotifications(prev => prev.filter(n => {
            const isForUser = String(n.userId) === String(studentId);
            const isMessageNotif = n.action?.type === 'view_messages' ||
                n.message.includes('Yeni mesaj') ||
                n.message.includes('okunmamış mesaj');

            // If it's for this user AND a message notification, remove it (return false)
            if (isForUser && isMessageNotif) return false;

            // Otherwise keep it
            return true;
        }));

        const student = students.find(s => s.id === studentId);
        if (!student) return;

        const hasUnread = student.messages.some(m => m.isAdmin && !m.read);
        if (!hasUnread) return;

        const updatedMessages = student.messages.map(m =>
            m.isAdmin ? { ...m, read: true } : m
        );
        const updatedStudent = { ...student, messages: updatedMessages };
        await handleUpdateStudent(updatedStudent);

        // Notify consultant that student read the messages
        if (socket && isConnected && student.assignedConsultantId) {
            socket.emit('messages_read', {
                to: student.assignedConsultantId,
                readerId: student.id
            });
        }
    };

    const handleMarkConsultantMessagesRead = async (studentId: number) => {
        const student = students.find(s => s.id === studentId);
        if (!student) return;

        const hasUnread = student.messages.some(m => !m.isAdmin && !m.read);
        if (!hasUnread) return;

        const updatedMessages = student.messages.map(m =>
            !m.isAdmin ? { ...m, read: true } : m
        );
        const updatedStudent = { ...student, messages: updatedMessages };
        await handleUpdateStudent(updatedStudent);

        // Notify student that consultant read the messages
        if (socket && isConnected) {
            socket.emit('messages_read', {
                to: studentId,
                readerId: user?.id
            });
        }
    };

    const addNotification = (targetUserId: any, msg: string, type: 'info' | 'success' | 'warning' = 'info', action?: any) => {
        console.log('[DEBUG addNotification] Called with:', { targetUserId, msg, type });
        console.trace('[DEBUG addNotification] Stack trace:');
        const userId = typeof targetUserId === 'string' ? parseInt(targetUserId) : targetUserId;
        setNotifications(prev => {
            const newNotification = {
                id: Date.now(),
                userId: userId,
                message: msg,
                read: false,
                type: type,
                action: action,
                date: new Date().toISOString()
            };
            const filtered = [...prev, newNotification];
            // Enforce limit of 50 to avoid cross-user notification displacement
            if (filtered.length > 50) {
                return filtered.slice(filtered.length - 50);
            }
            return filtered;
        });

        // Add to toast messages for popup display
        if (String(userId) === String(user?.id) || !user) {
            const toastId = Date.now() + Math.random();
            const newToast = {
                id: toastId,
                userId: userId,
                message: msg,
                read: false,
                type: type,
                action: action,
                date: new Date().toISOString()
            };
            setToastMessages(prev => [...prev, newToast]);
            setTimeout(() => {
                setToastMessages(prev => prev.filter(t => t.id !== toastId));
            }, 5000);
        }
    };

    const handleDeleteNotification = (id: number) => {
        setNotifications(prev => prev.filter(n => n.id !== id));
    };
    const sendSMS = (msg: string) => { setCurrentSMS(msg); setTimeout(() => setCurrentSMS(null), 6000); };


    const handleStudentAppointment = async (date: string, time: string, notes: string) => {
        if (!user || user?.role !== UserRole.STUDENT) return;

        const studentUser = user as Student;

        // Quota check logic
        const monthlyUsed = studentUser.meetingQuotas?.consultancy?.monthlyUsed || 0;
        const monthlyTotal = studentUser.meetingQuotas?.consultancy?.monthlyTotal || 4;
        
        if (monthlyUsed >= monthlyTotal) {
            addNotification(user?.id, "Aylık danışmanlık randevu kotanızı (4) doldurdunuz.", "warning");
            return;
        }

        const selDate = new Date(date);
        const getWeekNumber = (d: Date) => {
            const dCopy = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
            const dayNum = dCopy.getUTCDay() || 7;
            dCopy.setUTCDate(dCopy.getUTCDate() + 4 - dayNum);
            const yearStart = new Date(Date.UTC(dCopy.getUTCFullYear(),0,1));
            return Math.ceil((((dCopy.getTime() - yearStart.getTime()) / 86400000) + 1)/7);
        };

        const hasAptThisWeek = appointments.some(a => 
            String(a.studentId) === String(user.id) && 
            (a.type === 'consultant' || !a.type) && 
            getWeekNumber(new Date(a.date)) === getWeekNumber(selDate) &&
            new Date(a.date).getFullYear() === selDate.getFullYear()
        );

        if (hasAptThisWeek) {
            addNotification(user?.id, "Seçtiğiniz hafta için zaten bir danışman randevunuz bulunmaktadır. Haftada sadece 1 randevu alabilirsiniz.", "warning");
            return;
        }

        // Check if student has an assigned consultant
        if (!studentUser.assignedConsultantId) {
            addNotification(user?.id, "Danışman bilgisi eksik. Lütfen yöneticinize başvurun.", "warning");
            return;
        }

        // Find the assigned consultant
        const consultant = consultants.find(c => String(c.id) === String(studentUser.assignedConsultantId));

        if (!consultant) {
            addNotification(user?.id, "Atanan danışman bulunamadı. Lütfen yöneticinize başvurun.", "warning");
            return;
        }

        const meetLink = await getValidMeetLink(consultant.id, `Danışman Görüşmesi - ${user?.name}`, date, time);

        const newApt: Appointment = {
            id: Date.now(),
            studentId: user?.id,
            studentName: user?.name,
            educatorId: consultant.id,
            date: date,
            time: time,
            meetLink: meetLink,
            notes: notes,
            type: 'consultant',
            status: 'approved',
            createdBy: user?.id
        };

        try {
            // Fetch fresh consultant data to avoid overwriting recent profile updates (like email corrections)
            let freshConsultant = consultant;
            try {
                const fetched = await api.getConsultantById(consultant.id as number);
                if (fetched) freshConsultant = fetched;
            } catch (e) {
                console.error("Could not fetch fresh consultant data:", e);
            }

            const updatedConsultant = { ...freshConsultant, appointments: [...(freshConsultant.appointments || []), newApt] };
            await api.updateConsultant(updatedConsultant);
            setConsultants(prev => prev.map(c => c.id === consultant.id ? updatedConsultant : c));
            setAppointments(prev => [...prev.filter(a => a.id !== newApt.id), newApt]);
            addNotification(consultant.id as number, `${user?.name} yeni bir randevu oluşturdu: ${date} ${time}`, 'info');
            addNotification(user?.id, `Randevunuz başarıyla oluşturuldu: ${new Date(date).toLocaleDateString()} ${time}`, 'success');

            // Update User state
            const updatedQuotas = {
                ...studentUser.meetingQuotas,
                consultancy: {
                    ...(studentUser.meetingQuotas?.consultancy || {}),
                    monthlyTotal: 4,
                    monthlyUsed: (studentUser.meetingQuotas?.consultancy?.monthlyUsed || 0) + 1
                }
            };
            const updatedStudentUser = { ...studentUser, meetingQuotas: updatedQuotas };
            await handleUpdateStudent(updatedStudentUser as any);
        } catch (error) {
            console.error("Failed to create appointment:", error);
            addNotification(user?.id, "Randevu oluşturulamadı!", "warning");
        }
    };

    const handleTeacherAppointment = async (subject: string, date: string, time: string, notes: string) => {
        if (!user || user?.role !== UserRole.STUDENT) return;

        const studentUser = user as Student;

        // 1. Check if student has a private lesson package
        const hasPrivateLesson = studentUser.packageName?.toLowerCase().includes('özel ders') || (studentUser as any).package?.toLowerCase().includes('özel ders');

        if (!hasPrivateLesson) {
            addNotification(user?.id, "Özel ders talebinde bulunabilmek için lütfen paketinizi 'Özel Ders' içeren bir paketle yükseltin.", "warning");
            return;
        }

        // Quota check logic
        const monthlyUsed = studentUser.meetingQuotas?.privateTutoring?.monthlyUsed || 0;
        const monthlyTotal = studentUser.meetingQuotas?.privateTutoring?.monthlyTotal || 4;
        
        if (monthlyUsed >= monthlyTotal) {
            addNotification(user?.id, "Aylık özel ders randevu kotanızı (4) doldurdunuz.", "warning");
            return;
        }

        const selDate = new Date(date);
        const getWeekNumber = (d: Date) => {
            const dCopy = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
            const dayNum = dCopy.getUTCDay() || 7;
            dCopy.setUTCDate(dCopy.getUTCDate() + 4 - dayNum);
            const yearStart = new Date(Date.UTC(dCopy.getUTCFullYear(),0,1));
            return Math.ceil((((dCopy.getTime() - yearStart.getTime()) / 86400000) + 1)/7);
        };

        const hasAptThisWeek = appointments.some(a => 
            String(a.studentId) === String(user.id) && 
            a.type === 'teacher' && 
            getWeekNumber(new Date(a.date)) === getWeekNumber(selDate) &&
            new Date(a.date).getFullYear() === selDate.getFullYear()
        );

        if (hasAptThisWeek) {
            addNotification(user?.id, "Seçtiğiniz hafta için zaten bir özel ders randevunuz bulunmaktadır. Haftada sadece 1 özel ders alabilirsiniz.", "warning");
            return;
        }

        // 2. Check if student has an assigned consultant to route the request
        if (!studentUser.assignedConsultantId) {
            addNotification(user?.id, "Talebinizi işleme alacak danışman henüz atanmamış. Lütfen sisteme mesaj atarak destek isteyin.", "warning");
            return;
        }

        // Find the assigned consultant
        const consultant = consultants.find(c => String(c.id) === String(studentUser.assignedConsultantId));

        if (!consultant) {
            addNotification(user?.id, "Atanan danışman sistemde bulunamadı. Lütfen yöneticinize başvurun.", "warning");
            return;
        }

        const meetLink = await getValidMeetLink(consultant.id, `Özel Ders Talebi - ${subject} - ${user?.name}`, date, time);

        const newApt: Appointment = {
            id: Date.now(),
            studentId: user?.id,
            studentName: user?.name,
            educatorId: consultant.id, // Consultant manages the appointment
            date: date,
            time: time,
            meetLink: meetLink,
            notes: notes,
            type: 'teacher',
            subject: subject,
            status: 'approved',
            teacherId: undefined, // Will be set when admin assigns a teacher
            createdBy: user?.id
        };

        try {
            // Fetch fresh consultant data to avoid overwriting recent profile updates
            let freshConsultant = consultant;
            try {
                const fetched = await api.getConsultantById(consultant.id as number);
                if (fetched) freshConsultant = fetched;
            } catch (e) {
                console.error("Could not fetch fresh consultant data:", e);
            }

            const updatedConsultant = { ...freshConsultant, appointments: [...(freshConsultant.appointments || []), newApt] };
            await api.updateConsultant(updatedConsultant);
            setConsultants(prev => prev.map(c => c.id === consultant.id ? updatedConsultant : c));
            setAppointments(prev => [...prev.filter(a => a.id !== newApt.id), newApt]);
            addNotification(user?.id, `Özel ders talebiniz alındı: ${subject} - ${new Date(date).toLocaleDateString()} ${time}`, 'success');

            // Update User state
            const updatedQuotas = {
                ...studentUser.meetingQuotas,
                privateTutoring: {
                    ...(studentUser.meetingQuotas?.privateTutoring || {}),
                    monthlyTotal: 4,
                    monthlyUsed: (studentUser.meetingQuotas?.privateTutoring?.monthlyUsed || 0) + 1
                }
            };
            const updatedStudentUser = { ...studentUser, meetingQuotas: updatedQuotas };
            await handleUpdateStudent(updatedStudentUser as any);
        } catch (error) {
            console.error("Failed to create appointment:", error);
            addNotification(user?.id, "Randevu oluşturulamadı!", "warning");
        }
    };

    const handleAddAppointment = async (newApt: Appointment) => {
        if (consultants.length > 0) {
            const consultant = consultants[0];
            const updatedConsultant = { ...consultant, appointments: [...(consultant.appointments || []), newApt] };
            try {
                await api.updateConsultant(updatedConsultant);
                setConsultants(prev => prev.map(c => c.id === updatedConsultant.id ? updatedConsultant : c));
                setAppointments(prev => {
                    const otherApts = prev.filter(a => String(a.educatorId) !== String(updatedConsultant.id));
                    return [...otherApts, ...(updatedConsultant.appointments || [])];
                });
                addNotification(newApt.studentId as number, `Yeni randevu oluşturuldu: ${new Date(newApt.date).toLocaleDateString()} ${newApt.time} `, 'success');
            } catch (error) {
                console.error("Failed to add appointment:", error);
                addNotification(0, "Randevu oluşturulamadı!", "warning");
            }
        }
    };

    const handleCancelAppointment = async (appointmentId: number) => {
        if (!window.confirm('Randevuyu iptal etmek istediğinize emin misiniz?')) return;

        try {
            const apt = appointments.find(a => a.id === appointmentId);
            if (!apt) return;

            const isConsultant = apt.type === 'consultant' || !apt.type;
            const quotaKey = isConsultant ? 'consultancy' : 'privateTutoring';
            const studentUser = user as Student;
            const limit = studentUser.meetingQuotas?.[quotaKey]?.weeklyCancellationLimit || 1;
            const used = studentUser.meetingQuotas?.[quotaKey]?.weeklyCancellationsUsed || 0;
            
            if (used >= limit) {
                addNotification(user?.id || 0, "Haftalık iptal hakkınızı doldurdunuz. İşlem gerçekleştirilemedi.", "warning");
                return;
            }

            // Update local state first
            setAppointments(prev => prev.filter(a => String(a.id) !== String(appointmentId)));

            // Update Educator state (Consultant or Teacher)
            if (apt.educatorId) {
                const consultant = consultants.find(c => String(c.id) === String(apt.educatorId));
                if (consultant) {
                    const updatedConsultant = {
                        ...consultant,
                        appointments: (consultant.appointments || []).filter(a => String(a.id) !== String(appointmentId))
                    };
                    await api.updateConsultant(updatedConsultant);
                    setConsultants(prev => prev.map(c => c.id === consultant.id ? updatedConsultant : c));
                }

                const teacher = teachers.find(t => String(t.id) === String(apt.educatorId));
                if (teacher) {
                    const updatedTeacher = {
                        ...teacher,
                        appointments: (teacher.appointments || []).filter(a => String(a.id) !== String(appointmentId))
                    };
                    await api.updateTeacher(updatedTeacher);
                    setTeachers(prev => prev.map(t => t.id === teacher.id ? updatedTeacher : t));
                }
            }

            // Apply quota updates
            const updatedQuotas = {
                ...studentUser.meetingQuotas,
                [quotaKey]: {
                    ...(studentUser.meetingQuotas?.[quotaKey] || {}),
                    weeklyCancellationsUsed: (studentUser.meetingQuotas?.[quotaKey]?.weeklyCancellationsUsed || 0) + 1,
                    monthlyUsed: Math.max(0, (studentUser.meetingQuotas?.[quotaKey]?.monthlyUsed || 0) - 1)
                }
            };
            await handleUpdateStudent({ ...studentUser, meetingQuotas: updatedQuotas } as any);

            addNotification(user?.id || 0, "Randevunuz başarıyla iptal edildi.", "success");
        } catch (error) {
            console.error("Failed to cancel appointment:", error);
            addNotification(user?.id || 0, "Randevu iptal edilemedi!", "warning");
        }
    };

    const myNotifications = notifications.filter(n => String(n.userId) === String(user?.id));

    // State for login modal
    const [showLoginModal, setShowLoginModal] = useState(false);
    
    // Toast notifications state
    const [toastMessages, setToastMessages] = useState<NotificationItem[]>([]);

    // Check if user is pending approval
    const isPendingApproval = user &&
        (user?.role === UserRole.TEACHER || user?.role === UserRole.EDUCATION_CONSULTANT) &&
        (user as any).approvalStatus === 'pending';

    // Check if accessing via admin route (handle trailing slashes and index.html)
    const pathname = window.location.pathname.replace(/\/$/, ''); // Remove trailing slash

    // Separate admin login page from admin dashboard
    const isAdminLoginPage = pathname === '/admin-login' || pathname === '/admin-login/index.html' || window.location.hash.startsWith('#admin-login') || window.location.hash.startsWith('#/admin-login');
    const isAdminDashboard = pathname === '/admin' || pathname === '/admin/index.html' || window.location.hash.startsWith('#admin') || window.location.hash.startsWith('#/admin');
    const isAdminRoute = isAdminLoginPage || isAdminDashboard;

    // Check if accessing via educator route
    const isEducatorRoute = pathname === '/educator' || pathname === '/educator/index.html' || window.location.hash.startsWith('#educator') || window.location.hash.startsWith('#/educator');

    // Check if accessing via EduAnaliz route
    const isEduAnalizRoute = pathname === '/eduanaliz' || pathname === '/eduanaliz/index.html' || window.location.hash.startsWith('#eduanaliz') || window.location.hash.startsWith('#/eduanaliz');

    // Check if survey param is passed
    const hasAnketParam = new URLSearchParams(window.location.search).has('anket') || 
                          window.location.hash.includes('?anket=') || 
                          window.location.hash.includes('&anket=');

    // Check if accessing main route (student landing page)
    const isMainRoute = pathname === '' || pathname === '/' || pathname === '/index.html' || (pathname === '/' && !window.location.hash);

    // Check if accessing legal pages
    const isPrivacyRoute = pathname === '/privacy' || pathname === '/privacy/index.html' || window.location.hash === '#privacy' || window.location.hash === '#/privacy';
    const isTermsRoute = pathname === '/terms' || pathname === '/terms/index.html' || window.location.hash === '#terms' || window.location.hash === '#/terms';

    // E-posta doğrulama bağlantısı: /verify-email?token=...
    const isVerifyEmailRoute = pathname === '/verify-email' || pathname === '/verify-email/index.html'
        || window.location.hash.startsWith('#verify-email') || window.location.hash.startsWith('#/verify-email');

    const isSharedPlanRoute = pathname.startsWith('/shared-plan');
    const sharedStudentId = pathname.split('/shared-plan/')[1] || new URLSearchParams(window.location.search).get('id');

    if (isVerifyEmailRoute) {
        return <EduAnalizVerifyEmail />;
    }

    if (isSharedPlanRoute && sharedStudentId) {
        return <SharedCampPlanView studentId={sharedStudentId} />;
    }

    if (isPrivacyRoute) {
        return <PrivacyPolicy />;
    }

    if (isTermsRoute) {
        return <TermsOfService />;
    }


    // Handle special routes - clear sessions when accessing login pages
    useEffect(() => {
        // If no user, nothing to check
        if (!user) return;

        // Anyone (including admin) on admin LOGIN page should be logged out to see the login form
        // But only if they are not already a guest
        if (isAdminLoginPage) {
            console.log('Logging out: on admin login page, showing login form');
            localStorage.removeItem('supervisor_user');
            setUser(null);
            return;
        }

        // Non-educator users on educator route should be logged out
        // Wait, if it's an admin, we might want to allow them to see it if viewing as role
        // Added admin check to prevent loop when admin views as educator
        const isActuallyEducator = user?.role === UserRole.TEACHER || user?.role === UserRole.EDUCATION_CONSULTANT || user?.isEduAnaliz;
        const isAdminViewing = user?.role === UserRole.ADMIN || adminUser !== null;

        if (isEducatorRoute && !isActuallyEducator && !isAdminViewing) {
            console.log('Logging out: accessing educator route with non-educator user');
            localStorage.removeItem('supervisor_user');
            setUser(null);
            return;
        }

        if (isEduAnalizRoute && !user?.isEduAnaliz && !isAdminViewing) {
            console.log('Logging out: accessing eduanaliz route with non-eduanaliz user');
            localStorage.removeItem('supervisor_user');
            setUser(null);
            return;
        }
    }, [user?.role, user?.isEduAnaliz, isEduAnalizRoute, isAdminLoginPage, isEducatorRoute, adminUser]);

    // Check if test/exam params are passed (for QR code links)
    const hasTestParam = new URLSearchParams(window.location.search).has('test') ||
                          window.location.hash.includes('?test=') ||
                          window.location.hash.includes('&test=');
    const hasExamParam = new URLSearchParams(window.location.search).has('exam') ||
                          window.location.hash.includes('?exam=') ||
                          window.location.hash.includes('&exam=');

    // ── QR / LINK REDIRECT — anket, test, exam parametreleri her durumda çalışsın ──
    // Bu kontrol user login durumundan bağımsız olmalı (QR okutulan kişi farklı cihazdan geliyor)
    if (hasAnketParam || hasTestParam || hasExamParam) {
        return (
            <>
                <NotificationToast />
                <Suspense fallback={<LoadingFallback />}>
                    <EduAnalizWrapper 
                        user={null} 
                        onLogout={() => window.location.href = '/'} 
                    />
                </Suspense>
            </>
        );
    }


    // Registration view removed - RegistrationView component not implemented

    // Check for checkout page
    if (showCheckoutPage) {
        return (
            <>
                <NotificationToast />
                <Suspense fallback={<LoadingFallback />}>
                    <CheckoutPage
                        packageId={selectedCheckoutPackageId || ''}
                        onBack={() => {
                            setShowCheckoutPage(false);
                            setSelectedCheckoutPackageId(null);
                        }}
                        onComplete={(user) => {
                            handleLogin(user);
                            setShowCheckoutPage(false);
                            setSelectedCheckoutPackageId(null);
                        }}
                    />
                </Suspense>
            </>
        );
    }

    if (user && (user as any).mustChangePassword) {
        return (
            <ForcePasswordChangeModal
                user={user}
                onPasswordChanged={(updatedUser) => {
                    const { password, ...safeUser } = updatedUser as any;
                    setUser(safeUser);
                    localStorage.setItem('supervisor_user', JSON.stringify(safeUser));
                }}
            />
        );
    }

    if (!user) {
        // If there's an 'anket' parameter, we should directly show the EduAnaliz public survey form
        if (hasAnketParam) {
            return (
                <>
                    <NotificationToast />
                    <Suspense fallback={<LoadingFallback />}>
                        <EduAnalizWrapper 
                            user={null} 
                            onLogout={() => window.location.href = '/'} 
                        />
                    </Suspense>
                </>
            );
        }

        // If educator route, show educator login page
        if (isEducatorRoute) {
            return (
                <>
                    <NotificationToast />
                    <EducatorLoginPage onLogin={handleLogin} />
                </>
            );
        }

        // If eduanaliz route, show eduanaliz modal directly
        if (isEduAnalizRoute) {
            return (
                <>
                    <NotificationToast />
                    <EduAnalizLanding
                        onLoginSuccess={(eduUser) => {
                            handleLogin(eduUser);
                        }}
                        onBack={() => window.location.href = '/'}
                    />
                </>
            );
        }

        // If admin route, show admin-only login
        if (isAdminRoute) {
            return (
                <>
                    <NotificationToast />
                    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center p-4">
                        <div className="bg-white p-10 rounded-3xl shadow-2xl w-full max-w-md text-center">
                            <img src={LOGO_URL} className="w-24 h-24 mx-auto mb-6 rounded-2xl shadow-lg object-cover" alt="Supervisor Eğitim" />
                            <h1 className="text-2xl font-extrabold text-gray-900 mb-2">Admin Girişi</h1>
                            <p className="text-gray-500 mb-6 text-sm">Yönetici Paneli Erişimi</p>
                            <LoginModal
                                isOpen={true}
                                onClose={() => window.location.href = '/'}
                                onLogin={handleLogin}
                                isAdminRoute={true}
                            />
                        </div>
                    </div>
                </>
            );
        }

        return (
            <>
                <NotificationToast />
                <LandingPage
                    onLoginClick={() => {
                        // Yeni giriş yapmadan önce eski oturumu temizle
                        localStorage.removeItem('supervisor_user');
                        setUser(null);
                        setShowLoginModal(true);
                    }}
                    onPlanClick={(pkgId) => {
                        setSelectedCheckoutPackageId(pkgId);
                        setShowCheckoutPage(true);
                    }}
                    onEduAnalizClick={() => window.location.href = '/eduanaliz'}
                />
                <LoginModal
                    isOpen={showLoginModal}
                    onClose={() => setShowLoginModal(false)}
                    onLogin={handleLogin}
                    isMainRoute={true}
                />
                <EduAnalizLoginModal
                    isOpen={showEduAnalizLoginModal}
                    onClose={() => setShowEduAnalizLoginModal(false)}
                    onAccess={(eduUser) => {
                        setUser(eduUser);
                        setShowEduAnalizLoginModal(false);
                    }}
                />
            </>
        );
    }

    // Admin Dashboard - Only rendered if on an admin route
    if (user?.role === UserRole.ADMIN && isAdminRoute) {
        return (
            <>
                <NotificationToast />
                <Suspense fallback={<LoadingFallback />}>
                    <AdminDashboard
                    onLogout={() => {
                        localStorage.removeItem('user');
                        setUser(null);
                    }}
                    onViewAsRole={(role, selectedUser) => {
                        // ... (view as role logic remains same)
                        // Store current admin user for returning later
                        setAdminUser(user);

                        // Use selected user if provided, otherwise use defaults
                        if (role === 'student') {
                            const targetUser = selectedUser || students[0] || {
                                id: 999,
                                name: 'Demo Öğrenci',
                                email: 'demo@supervisor.com',
                                grade: '12. Sınıf',
                                department: 'Sayısal',
                                goal: 'Demo Hedef',
                                onboarded: true,
                                trialExams: [],
                                weeklyPlans: [],
                                messages: [],
                                resources: [],
                                points: 100,
                                streak: 1,
                                approvalStatus: 'approved'
                            };
                            setUser({ ...targetUser, role: UserRole.STUDENT } as any);
                        } else if (role === 'consultant') {
                            const targetUser = selectedUser || consultants[0] || {
                                id: 998,
                                name: 'Demo Danışman',
                                email: 'danisman@supervisor.com',
                                approvalStatus: 'approved',
                                students: [],
                                dailyNotes: []
                            };
                            setUser({ ...targetUser, role: UserRole.EDUCATION_CONSULTANT } as any);
                        } else if (role === 'teacher') {
                            const targetUser = selectedUser || {
                                id: 997,
                                name: 'Demo Öğretmen',
                                email: 'ogretmen@supervisor.com',
                                branch: 'Matematik',
                                approvalStatus: 'approved',
                                students: []
                            };
                            setUser({ ...targetUser, role: UserRole.TEACHER } as any);
                        } else if (role === 'parent') {
                            // selectedUser is the student whose parent dashboard we want to view
                            const studentForParent = selectedUser || students[0];
                            const targetParent = {
                                id: 996,
                                name: studentForParent ? `${studentForParent.name}'in Velisi` : 'Demo Veli',
                                email: studentForParent ? `veli.${studentForParent.email}` : 'veli@supervisor.com',
                                studentIds: studentForParent ? [studentForParent.id] : [1],
                                phoneNumber: '0555 555 55 55',
                                notifications: { sms: true, email: true }
                            };
                            setUser({ ...targetParent, role: UserRole.PARENT } as any);
                        }
                    }}
                />
                </Suspense>
            </>
        );
    }

    // If Admin but NOT on admin route, show Landing Page like a guest (student entry point)
    if (user?.role === UserRole.ADMIN && !isAdminRoute) {
        return (
            <>
                <NotificationToast />
                <LandingPage 
                    onLoginClick={() => {
                        // Admin can log in as student from here if they wish (after logout)
                        localStorage.removeItem('user');
                        setUser(null);
                        setShowLoginModal(true);
                    }} 
                    onPlanClick={(pkgId) => {
                        setSelectedCheckoutPackageId(pkgId);
                        setShowCheckoutPage(true);
                    }}
                    onEduAnalizClick={() => window.location.href = '/eduanaliz'}
                />
                <LoginModal
                    isOpen={showLoginModal}
                    onClose={() => setShowLoginModal(false)}
                    onLogin={handleLogin}
                />
                <EduAnalizLoginModal
                    isOpen={showEduAnalizLoginModal}
                    onClose={() => setShowEduAnalizLoginModal(false)}
                    onAccess={(eduUser) => {
                        setUser(eduUser);
                        setShowEduAnalizLoginModal(false);
                    }}
                />
            </>
        );
    }

    // 1. Check if user is EduAnaliz (Highest priority for these users)
    if (user?.isEduAnaliz) {
        return (
            <>
                <NotificationToast />
                {adminUser && (
                    <button
                        onClick={() => {
                            setUser(adminUser);
                            setAdminUser(null);
                        }}
                        className="fixed bottom-6 right-6 z-[999] flex items-center gap-2 px-5 py-3 bg-gradient-to-r from-red-500 to-orange-500 text-white font-bold rounded-full shadow-2xl hover:scale-105 transform transition-all animate-pulse"
                        title="Admin Paneline Dön"
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 15l-3-3m0 0l3-3m-3 3h8M3 12a9 9 0 1118 0 9 9 0 01-18 0z" />
                        </svg>
                        Admin'e Dön
                    </button>
                )}
                <Suspense fallback={<LoadingFallback />}>
                    <EduAnalizWrapper 
                        user={user} 
                        onLogout={() => handleLogout()} 
                        // Profil ekranında değişen ad / kurum adı / fotoğraf.
                        // Oturum kopyası burada güncellenmezse yan menü ve
                        // üst çubuk eski değeri göstermeye devam ediyor —
                        // localStorage'daki kopya da eski kalıyor, sayfa
                        // yenilense bile düzelmiyordu.
                        onUserUpdate={(patch: any) =>
                            setUser(prev => (prev ? { ...prev, ...patch } : prev))}
                    />
                </Suspense>
            </>
        );
    }

    if (user?.role === UserRole.STUDENT && !(user as Student).onboarded) {
        return (
            <Suspense fallback={<LoadingFallback />}>
                <OnboardingWizard student={user as Student} onComplete={(s) => { handleUpdateStudent(s); }} onExit={() => setUser(null)} />
            </Suspense>
        );
    }

    // Package gate: students without active package see package selection
    if (user?.role === UserRole.STUDENT && (user as Student).packageStatus !== 'active') {
        return <PackageSelectionScreen student={user as Student} onLogout={() => setUser(null)} />;
    }

    if (showProfilePage && user) {
        return <ProfileView user={user} onUpdate={handleUpdateProfile} onBack={() => setShowProfilePage(false)} />;
    }



    return (
        <>
            {/* Custom Toast Notifications Container */}
            <div className="fixed top-4 left-1/2 -translate-x-1/2 z-[100] flex flex-col items-center gap-3 w-full max-w-md pointer-events-none px-4">
                {toastMessages.map(toast => (
                    <div key={toast.id} className="bg-white/90 backdrop-blur-xl dark:bg-slate-800/90 rounded-2xl shadow-2xl border border-gray-200/50 dark:border-slate-700/50 p-4 animate-scaleIn pointer-events-auto transform transition-all relative overflow-hidden w-full ring-1 ring-black/5 dark:ring-white/10">
                        <div className={`absolute left-0 top-0 bottom-0 w-1.5 ${toast.type === 'success' ? 'bg-green-500' : toast.type === 'warning' ? 'bg-amber-500' : 'bg-blue-500'}`}></div>
                        <div className="flex items-start gap-3 pl-2">
                            <div className={`mt-0.5 w-7 h-7 rounded-full flex items-center justify-center shrink-0 shadow-sm ${toast.type === 'success' ? 'bg-gradient-to-br from-green-400 to-green-600 text-white' : toast.type === 'warning' ? 'bg-gradient-to-br from-amber-400 to-amber-600 text-white' : 'bg-gradient-to-br from-blue-400 to-blue-600 text-white'}`}>
                                {toast.type === 'success' ? <CheckCircleIcon className="w-4 h-4" /> : toast.type === 'warning' ? <ExclamationTriangleIcon className="w-4 h-4" /> : <BellIcon className="w-4 h-4" />}
                            </div>
                            <div className="flex-1 pr-6">
                                <p className="text-gray-800 dark:text-gray-100 font-bold text-sm leading-relaxed">
                                    {toast.message}
                                </p>
                                <p className="text-[10px] text-gray-500 dark:text-gray-400 mt-1.5 flex items-center gap-1 font-medium">
                                    <ClockIcon className="w-3 h-3 opacity-70" />
                                    {new Date(toast.date).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}
                                </p>
                            </div>
                            <button onClick={() => setToastMessages(prev => prev.filter(t => t.id !== toast.id))} className="absolute top-3 right-3 p-1.5 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-full transition-colors">
                                <XMarkIcon className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                ))}
            </div>
            <NotificationToast />
            {/* Floating Back to Admin Button */}
            {adminUser && (
                <button
                    onClick={() => {
                        setUser(adminUser);
                        setAdminUser(null);
                    }}
                    className="fixed bottom-6 right-6 z-[999] flex items-center gap-2 px-5 py-3 bg-gradient-to-r from-red-500 to-orange-500 text-white font-bold rounded-full shadow-2xl hover:scale-105 transform transition-all animate-pulse"
                    title="Admin Paneline Dön"
                >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 15l-3-3m0 0l3-3m-3 3h8M3 12a9 9 0 1118 0 9 9 0 01-18 0z" />
                    </svg>
                    Admin'e Dön
                </button>
            )}
            {showProfileModal && user && <ProfileEditModal user={user} onClose={() => setShowProfileModal(false)} onUpdate={handleUpdateProfile} />}
            {currentSMS && <SMSNotification message={currentSMS} onClose={() => setCurrentSMS(null)} />}

            {/* Educator Dashboards - Show if on educator route OR if admin is viewing as this role */}
            {user?.role === UserRole.EDUCATION_CONSULTANT && (isEducatorRoute || adminUser) ? (
                isPendingApproval ? (
                    <PendingApprovalBanner userType="consultant" />
                ) : (
                    <Suspense fallback={<LoadingFallback />}>
                        <ConsultantDashboard
                        students={students.filter(s => String(s.assignedConsultantId) === String(user?.id))}
                        onUpdateStudent={handleUpdateStudent}
                        onLogout={handleLogout}
                        appointments={appointments.filter(a => String(a.educatorId) === String(user?.id))}
                        onAddAppointment={handleAddAppointment}
                        onUpdateAppointment={async (apt) => {
                            // Mock update logic for appointment
                            const consultant = consultants[0];
                            const text = apt.summary || "";
                            // In real app, call api.updateAppointment
                            console.log("Updating appointment", apt);
                            // For now just update local state slightly
                            setAppointments(prev => prev.map(a => a.id === apt.id ? apt : a));
                        }}
                        onToggleTopic={handleToggleTopic}
                        onToggleResourceTopic={handleToggleResourceTopic}
                        notifications={notifications}
                        onSendMessage={(studentId, text) => handleSendMessage(studentId, text, true)}
                        user={user as EducationConsultant}
                        examLibrary={examLibrary}
                        onSaveExam={handleSaveExam}
                        onUpdateConsultant={handleUpdateProfile}
                        onMarkMessagesRead={handleMarkConsultantMessagesRead}
                        selectedStudentId={selectedStudentId}
                        setSelectedStudentId={setSelectedStudentId}
                        activeTab={educatorTab}
                        setActiveTab={setEducatorTab}
                    />
                    </Suspense>
                )
            ) : user?.role === UserRole.TEACHER && (isEducatorRoute || adminUser) ? (
                isPendingApproval ? (
                    <PendingApprovalBanner userType="teacher" />
                ) : (
                    <Suspense fallback={<LoadingFallback />}>
                        <TeacherDashboard
                        teacher={user as Teacher}
                        students={students}
                        appointments={appointments.filter(a => a.type === 'teacher' && String(a.teacherId) === String(user?.id))}
                        onLogout={handleLogout}
                        onUpdateTeacher={(updatedTeacher: Teacher) => {
                            setUser(updatedTeacher);
                            // In real app, also call api.updateTeacher
                        }}
                        onUpdateAppointment={(apt) => {
                            setAppointments(prev => prev.map(a => a.id === apt.id ? apt : a));
                        }}
                        setActiveView={setActiveView}
                        handleDeleteNotification={handleDeleteNotification}
                    />
                    </Suspense>
                )
            ) : user?.role === UserRole.PARENT ? (
                <Suspense fallback={<LoadingFallback />}>
                    <ParentDashboard
                        parent={user as Parent}
                        students={students}
                        appointments={appointments}
                        onLogout={handleLogout}
                        onSendMessage={(studentId, text) => handleSendMessage(studentId, text, false)}
                    />
                </Suspense>
            ) : (user?.role === UserRole.EDUCATION_CONSULTANT || user?.role === UserRole.TEACHER) && !isEducatorRoute && !adminUser ? (
                /* Educator on main route (not via admin view) - show Landing Page */
                <>
                    <NotificationToast />
                    <LandingPage 
                        onLoginClick={() => {
                            localStorage.removeItem('supervisor_user');
                            setUser(null);
                            setShowLoginModal(true);
                        }} 
                        onPlanClick={(pkgId) => {
                            setSelectedCheckoutPackageId(pkgId);
                            setShowCheckoutPage(true);
                        }}
                        onEduAnalizClick={() => window.location.href = '/eduanaliz'}
                    />
                    <LoginModal
                        isOpen={showLoginModal}
                        onClose={() => setShowLoginModal(false)}
                        onLogin={handleLogin}
                    />
                    <EduAnalizLoginModal
                        isOpen={showEduAnalizLoginModal}
                        onClose={() => setShowEduAnalizLoginModal(false)}
                        onAccess={(eduUser) => {
                            setUser(eduUser);
                            setShowEduAnalizLoginModal(false);
                        }}
                    />
                </>
            ) : (
                <div className="flex h-screen bg-gray-50 dark:bg-[#020617] dark:bg-grid-white/[0.02] font-sans relative transition-colors duration-300">
                    {/* Mobile Overlay */}
                    {isSidebarOpen && (
                        <div
                            className="fixed inset-0 bg-black/50 z-40 md:hidden backdrop-blur-sm transition-opacity"
                            onClick={() => setIsSidebarOpen(false)}
                        />
                    )}

                    <div className={`
                        fixed inset-y-0 left-0 z-50 w-72 bg-white dark:bg-[#0F172A] border-r border-gray-200 dark:border-white/5 flex flex-col items-center md:items-stretch py-6 no-print
                        transform transition-all duration-300 ease-in-out shadow-2xl md:shadow-none overflow-hidden
                        ${isSidebarOpen ? 'translate-x-0 md:w-72' : '-translate-x-full md:w-0 md:border-none md:p-0'}
                        md:relative md:translate-x-0 md:transform-none
                    `}>
                        <div className="w-72 flex flex-col h-full px-6">
                            <div className="mb-10 flex items-center justify-between md:justify-start gap-3">
                                <div className="flex items-center gap-3">
                                    <img src={LOGO_URL} className="w-10 h-10 rounded-lg" alt="Logo" />
                                    <span className="text-2xl font-extrabold text-gray-900 dark:text-white tracking-tight">Supervisor Eğitim</span>
                                </div>
                                <button onClick={() => setIsSidebarOpen(false)} title="Menüyü Kapat" className="md:hidden text-gray-500"><XMarkIcon className="w-6 h-6" /></button>
                            </div>
                            <nav className="flex-1 space-y-2 w-full">
                                {[
                                    { id: 'home', icon: HomeIcon, label: 'Ana Sayfa' },
                                    { id: 'target', icon: TargetIcon, label: 'Hedef Analizi' },
                                    { id: 'plan', icon: CalendarIcon, label: 'Çalışma Planım' },
                                    { id: 'exams', icon: ChartBarIcon, label: 'Deneme Takibi' },
                                    { id: 'topic-history', icon: ChartBarIcon, label: 'Soru Dağılımı' },
                                    { id: 'teach', icon: AcademicCapIcon, label: 'Sor Öğren' },
                                    { id: 'scales', icon: ClipboardListIcon, label: 'Akademik & Kariyer' },
                                    { id: 'communication', icon: ChatIcon, label: 'İletişim & Randevu' }
                                ].map(item => {
                                    const studentUnreadMessages = item.id === 'communication' && user?.role === UserRole.STUDENT
                                        ? (user as Student).messages?.filter(m => m.isAdmin && !m.read).length
                                        : 0;

                                    return (
                                        <button key={item.id} onClick={() => { setActiveView(item.id); setIsSidebarOpen(false); }} className={`w-full flex items-center gap-3 px-4 py-3.5 rounded-xl transition-all duration-200 group relative ${activeView === item.id ? 'bg-blue-600 text-white shadow-lg shadow-blue-200 dark:shadow-blue-900/30' : 'text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-slate-700/50 hover:text-gray-900 dark:hover:text-white'} `}>
                                            <div className="relative">
                                                <item.icon className="w-6 h-6" />
                                                {studentUnreadMessages > 0 && (
                                                    <span className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-red-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center animate-pulse border border-white dark:border-[#0F172A]">
                                                        {studentUnreadMessages}
                                                    </span>
                                                )}
                                            </div>
                                            <span className="font-bold">{item.label}</span>
                                            {studentUnreadMessages > 0 && activeView !== item.id && (
                                                <span className="ml-auto w-2 h-2 bg-red-500 rounded-full md:hidden"></span>
                                            )}
                                        </button>
                                    );
                                })}

                            </nav>

                            {/* Sidebar Subscription Info */}
                            {user?.role === UserRole.STUDENT && (user as Student).packageStatus === 'active' && (
                                <div className="mt-6 mb-2 p-4 bg-blue-50/50 dark:bg-blue-500/5 rounded-2xl border border-blue-100/50 dark:border-blue-500/10">
                                    <div className="flex items-center gap-3 mb-2">
                                        <div className="p-2 bg-blue-600 rounded-lg shadow-lg shadow-blue-600/20">
                                            <BookmarkIcon className="w-4 h-4 text-white" />
                                        </div>
                                        <div>
                                            <p className="text-[10px] uppercase font-black text-blue-600 dark:text-blue-400 tracking-wider">Abonelik</p>
                                            <p className="text-sm font-bold text-gray-900 dark:text-white truncate max-w-[140px]">{(user as Student).packageName}</p>
                                        </div>
                                    </div>
                                    <div className="space-y-1.5">
                                        <div className="flex justify-between text-[10px]">
                                            <span className="text-gray-500 font-bold uppercase">Kalan Süre</span>
                                            <span className="text-blue-600 font-black uppercase">
                                                {(user as Student).packageExpiryDate
                                                    ? Math.max(0, Math.ceil((new Date((user as Student).packageExpiryDate).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)))
                                                    : 0} GÜN
                                            </span>
                                        </div>
                                        <div className="w-full h-1.5 bg-blue-100 dark:bg-blue-900/20 rounded-full overflow-hidden">
                                            <div
                                                className="h-full bg-blue-600 rounded-full animate-pulse-slow"
                                                style={{
                                                    width: `${(user as Student).packageExpiryDate
                                                        ? Math.min(100, (Math.max(0, Math.ceil((new Date((user as Student).packageExpiryDate).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24))) / 30) * 100)
                                                        : 0}%`
                                                }}
                                            />
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div className="mt-auto py-4"><button onClick={() => setUser(null)} className="w-full flex items-center gap-3 px-4 py-3 text-red-500 hover:bg-red-50 rounded-xl transition-colors"><LogoutIcon className="w-6 h-6" /><span className="font-bold">Çıkış Yap</span></button></div>
                        </div>
                    </div>
                    <div className="flex-1 flex flex-col overflow-hidden relative">
                        <header className="bg-white dark:bg-[#0F172A] border-b border-gray-200 dark:border-white/5 h-20 px-4 md:px-8 flex items-center justify-between shrink-0 no-print transition-colors duration-300">
                            <div className="flex items-center gap-3">
                                <button title="Menü" onClick={() => setIsSidebarOpen(!isSidebarOpen)} className="text-gray-600 p-2 -ml-2 hover:bg-gray-100 rounded-lg transition-colors"><MenuIcon className="w-6 h-6" /></button>
                                <h2 className="text-xl md:text-2xl font-bold text-gray-800 dark:text-white capitalize">{activeView === 'home' ? 'Genel Bakış' : activeView === 'class-assignments' ? 'Sınıf Testleri' : activeView === 'target' ? 'Hedef Analizi' : activeView === 'plan' ? 'Çalışma Planım' : activeView === 'tracker' ? 'Konu Bitirme Durumu' : activeView === 'exams' ? 'Deneme Takibi' : activeView === 'topic-history' ? 'Soru Dağılımı' : activeView === 'teach' ? 'Sor Öğren' : activeView === 'scales' ? 'Akademik & Kariyer' : 'İletişim & Randevu'}</h2>
                            </div>
                            <div className="flex items-center gap-4">
                                <button
                                    onClick={() => setDarkMode(!darkMode)}
                                    className={`p-2 rounded-xl transition-all duration-300 ${darkMode ? 'bg-indigo-500/20 text-indigo-300' : 'bg-orange-100 text-orange-500 hover:bg-orange-200'}`}
                                >
                                    {darkMode ? <MoonIcon className="w-6 h-6" /> : <SunIcon className="w-6 h-6" />}
                                </button>
                                <div className="relative">
                                    <button
                                        onClick={() => setShowNotificationsDropdown(!showNotificationsDropdown)}
                                        className={`p-2 relative rounded-xl transition-colors ${showNotificationsDropdown ? 'text-blue-600 bg-blue-50 dark:bg-blue-900/20' : 'text-gray-400 hover:text-blue-600 hover:bg-gray-100 dark:hover:bg-white/5'}`}
                                    >
                                        <BellIcon className="w-6 h-6" />
                                        {myNotifications.length > 0 && (
                                            <span className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white ring-2 ring-white dark:ring-[#0F172A] animate-pulse">
                                                {myNotifications.length}
                                            </span>
                                        )}
                                    </button>

                                    {/* Notification Dropdown */}
                                    {showNotificationsDropdown && (
                                        <>
                                            <div className="fixed inset-0 z-30" onClick={() => setShowNotificationsDropdown(false)}></div>
                                            <div className="absolute right-0 top-full mt-3 w-80 md:w-96 bg-white dark:bg-slate-800 rounded-2xl shadow-xl border border-gray-100 dark:border-slate-700 z-40 overflow-hidden animate-scaleIn origin-top-right">
                                                <div className="p-4 border-b border-gray-100 dark:border-slate-700 flex justify-between items-center bg-gray-50/50 dark:bg-slate-800/50">
                                                    <h3 className="font-bold text-gray-900 dark:text-white text-sm">Bildirimler</h3>
                                                    {myNotifications.length > 0 && (
                                                        <span className="text-xs font-semibold text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30 px-2 py-1 rounded-full">{myNotifications.length} Yeni</span>
                                                    )}
                                                </div>
                                                <div className="max-h-[350px] overflow-y-auto custom-scrollbar">
                                                    {myNotifications.length === 0 ? (
                                                        <div className="p-10 text-center flex flex-col items-center justify-center text-gray-400 dark:text-gray-500">
                                                            <div className="w-16 h-16 bg-gray-50 dark:bg-slate-700/50 rounded-full flex items-center justify-center mb-4">
                                                                <BellIcon className="w-8 h-8 opacity-40" />
                                                            </div>
                                                            <p className="text-sm font-medium">Henüz bildiriminiz yok.</p>
                                                            <p className="text-xs mt-1 opacity-70">Önemli gelişmeler burada görünecek.</p>
                                                        </div>
                                                    ) : (
                                                        <div className="divide-y divide-gray-50 dark:divide-slate-700/50">
                                                            {[...myNotifications].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()).map(n => (
                                                                <div key={n.id} onClick={() => handleNotificationClick(n)} className="p-4 hover:bg-gray-50 dark:hover:bg-slate-700/30 transition-colors cursor-pointer group relative">
                                                                    <div className="flex gap-3">
                                                                        <div className={`w-2 h-2 rounded-full mt-2 shrink-0 ${n.type === 'success' ? 'bg-green-500' : n.type === 'warning' ? 'bg-amber-500' : 'bg-blue-500'}`}></div>
                                                                        <div className="flex-1">
                                                                            <p className="text-sm font-medium text-gray-800 dark:text-gray-200 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors pr-6 whitespace-pre-wrap">
                                                                                {n.message}
                                                                            </p>
                                                                            <p className="text-[10px] text-gray-400 mt-1.5 flex items-center gap-1">
                                                                                <ClockIcon className="w-3 h-3" />
                                                                                {new Date(n.date).toLocaleDateString('tr-TR')} {new Date(n.date).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}
                                                                            </p>
                                                                        </div>
                                                                    </div>
                                                                    <button
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            handleDeleteNotification(n.id);
                                                                        }}
                                                                        className="absolute top-4 right-4 text-gray-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all p-1"
                                                                        title="Bildirimi Sil"
                                                                    >
                                                                        <XCircleIcon className="w-4 h-4" />
                                                                    </button>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                                {myNotifications.length > 0 && (
                                                    <div className="p-3 border-t border-gray-100 dark:border-slate-700 bg-gray-50 dark:bg-slate-800/50 text-center">
                                                        <button
                                                            onClick={() => setShowNotificationsDropdown(false)}
                                                            className="text-xs font-bold text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
                                                        >
                                                            Kapat
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        </>
                                    )}
                                </div><div onClick={() => setShowProfilePage(true)} className="flex items-center gap-3 pl-4 border-l cursor-pointer hover:bg-gray-50 dark:hover:bg-white/5 p-2 rounded-xl transition-colors"><div className="text-right hidden md:block"><p className="text-sm font-bold text-gray-900 dark:text-gray-100">{user?.name}</p><p className="text-xs text-gray-500 dark:text-gray-400">{(user as Student).grade}</p></div><div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center text-white font-bold shadow-md">{user?.name.charAt(0)}</div></div></div></header>
                        <main className="flex-1 overflow-y-auto p-4 md:p-8">
                            <div className="max-w-7xl mx-auto h-full">
                                {activeView === 'home' && <StudentHomeView student={user as Student} appointments={appointments} onBookAppointment={handleStudentAppointment} notifications={myNotifications} yearlyRoadmap={yearlyRoadmap} onUpdateStudent={handleUpdateStudent} onOpenProfileAnalysis={() => setActiveView('scales')} quizStats={quizStats} onNavigate={(view, tab) => { setActiveView(view); if (view === 'plan' && tab) setPlanningTab(tab); }} onDeleteNotification={handleDeleteNotification} statsWeekOffset={statsWeekOffset} setStatsWeekOffset={setStatsWeekOffset} statsViewMode={statsViewMode} setStatsViewMode={setStatsViewMode} />}
                                {activeView === 'class-assignments' && (
                                    <ClassAssignmentsView
                                        student={user as Student}
                                        onStartQuiz={async (quizId) => {
                                            try {
                                                const quiz = await api.getTopicQuiz(quizId);
                                                setActiveTopicQuiz(quiz);
                                            } catch (err) {
                                                alert('Test yüklenirken hata oluştu.');
                                            }
                                        }}
                                        onOpenOptical={(quizId) => setActiveQuizForOptical(quizId)}
                                    />
                                )}
                                {activeView === 'target' && <TargetAnalysisView student={user as Student} onUpdateStudent={handleUpdateStudent} />}
                                {activeView === 'plan' && <StudyPlanView student={user as Student} onUpdateStudent={handleUpdateStudent} onToggle={(key, topicRes, questionRes) => handleToggleTopic(user?.id, key, topicRes, questionRes)} onToggleResourceTopic={(subject, topicKey, resourceId) => handleToggleResourceTopic(user?.id, subject, topicKey, resourceId)} initialTab={planningTab} onOpenTeachView={(settings) => { setTeachViewSettings(settings as any); setActiveView('teach'); }} />}
                                {activeView === 'exams' && <ExamTrackingView student={user as Student} examLibrary={examLibrary} onUpdateStudent={handleUpdateStudent} />}
                                {activeView === 'topic-history' && <TopicHistoryView studentGrade={(user as Student).grade} />}
                                {activeView === 'teach' && (
                                    <TeachView
                                        student={user as Student}
                                        onClose={() => {
                                            setActiveView('home');
                                            setTeachViewSettings(null);
                                        }}
                                        onStartExam={handleStartExam}
                                        onReviewExam={handleReviewExam}
                                        quizStats={quizStats}
                                        onRefreshStats={(examType) => fetchQuizStats(String(user?.id), examType)}
                                        initialSection={teachViewSettings?.section}
                                        initialSubject={teachViewSettings?.subject}
                                        initialExamType={teachViewSettings?.examType}
                                        initialTopic={teachViewSettings?.topic}
                                        onInitialTopicProcessed={() => {
                                            // Clear the initial topic from state so it doesn't re-trigger on refreshes
                                            setTeachViewSettings(prev => prev ? { ...prev, topic: null } : null);
                                        }}
                                    />
                                )}
                                {activeView === 'scales' && <SupervisorView student={user as Student} onClose={() => setActiveView('home')} initialTab="scales" onUpdateStudent={handleUpdateStudent} />}
                                {activeView === 'communication' && (
                                    <StudentCommunicationView
                                        student={user as Student}
                                        appointments={appointments}
                                        onBookAppointment={handleStudentAppointment}
                                        onSendMessage={(text) => handleSendMessage(user?.id, text, false)}
                                        onBookTeacherAppointment={handleTeacherAppointment}
                                        onCancelAppointment={handleCancelAppointment}
                                        onOpenRatingModal={(apt) => {
                                            const aptDateTime = new Date(apt.date + 'T' + apt.time);
                                            if (aptDateTime > new Date()) {
                                                alert('Bu görüşme henüz gerçekleşmedi. Görüşme saati geçtikten sonra değerlendirme yapabilirsiniz.');
                                                return;
                                            }
                                            setPendingRatingAppointment(apt);
                                        }}
                                        onMarkRead={() => handleMarkStudentMessagesRead(user?.id)}
                                        blockedSlots={consultants[0]?.blockedSlots || []}
                                        consultantName={consultants.find(c => String(c.id) === String((user as Student).assignedConsultantId))?.name}
                                    />
                                )}
                            </div>
                        </main>
                    </div>
                </div>
            )}
            {reviewExam && (
                <ExamRunner
                    exam={reviewExam.exam}
                    onFinish={() => { }} // No-op for review
                    onExit={() => setReviewExam(null)}
                    mode="review"
                    initialAnswers={reviewExam.result.studentAnswers}
                />
            )}
            {activeExam && (
                <ExamRunner
                    exam={activeExam}
                    onFinish={handleFinishExam}
                    onExit={() => {
                        if (window.confirm('Sınavdan çıkmak istediğinize emin misiniz? Kaydedilmemiş ilerlemeniz silinecek.')) {
                            setActiveExam(null);
                        }
                    }}
                />
            )}
            {/* Meeting Rating Modal - Shows after completed meetings for students */}
            {pendingRatingAppointment && user?.role === UserRole.STUDENT && (
                <MeetingRatingModal
                    appointment={pendingRatingAppointment}
                    onSubmit={handleSubmitRating}
                    onClose={() => setPendingRatingAppointment(null)}
                    onSkip={handleSkipRating}
                    onReportNoShow={handleReportNoShow}
                />
            )}



            {activeTopicQuiz && user && (
                <TopicQuizModal
                    quiz={activeTopicQuiz}
                    studentId={user?.id}
                    studentName={user?.name}
                    onClose={() => setActiveTopicQuiz(null)}
                />
            )}

            {activeQuizForOptical && user && (
                <VirtualOpticalForm
                    quizId={activeQuizForOptical}
                    studentId={String(user?.id)}
                    onClose={() => setActiveQuizForOptical(null)}
                />
            )}

            <EduAnalizLoginModal
                isOpen={showEduAnalizLoginModal}
                onClose={() => setShowEduAnalizLoginModal(false)}
                onAccess={(eduUser) => {
                    setUser(eduUser);
                    setShowEduAnalizLoginModal(false);
                }}
            />

            <CookieConsent />
        </>
    );
};



// Wrap App with NotificationProvider and SocketProvider
const AppWithNotifications = () => (
    <NotificationProvider>
        <SocketProvider>
            <App />
        </SocketProvider>
    </NotificationProvider>
);

export default AppWithNotifications;

